import { useEffect, useState } from 'react';
import NetInfo, { NetInfoState } from '@react-native-community/netinfo';

/**
 * Hook que reporta el estado de conectividad del dispositivo.
 *
 * Devuelve:
 *   - isOnline:   true si hay internet usable (wifi o celular conectado a red)
 *   - isFast:     true si la conexión es rápida (4G/5G/wifi) o false (2G/3G/edge)
 *   - kind:       'wifi' | 'cellular' | 'none' | 'unknown'
 *
 * Útil para:
 *   - Mostrar banner "Sin conexión — modo offline"
 *   - Cambiar a modo de tracking GPS más lento
 *   - Reducir frecuencia de calls a Cloud Functions
 *   - Mostrar mensaje "Ruta del chofer puede tardar más sin internet"
 */

export interface NetworkStatus {
  isOnline: boolean;
  isFast: boolean;
  kind: 'wifi' | 'cellular' | 'none' | 'unknown';
}

const FAST_CELLULAR_GENERATIONS = new Set(['4g', '5g']);

function parseNetInfo(state: NetInfoState): NetworkStatus {
  const isOnline = !!state.isConnected && state.isInternetReachable !== false;

  let kind: NetworkStatus['kind'] = 'unknown';
  if (state.type === 'wifi') kind = 'wifi';
  else if (state.type === 'cellular') kind = 'cellular';
  else if (state.type === 'none') kind = 'none';

  let isFast = false;
  if (kind === 'wifi') {
    isFast = true;
  } else if (kind === 'cellular' && state.details) {
    const gen = (state.details as any)?.cellularGeneration;
    if (gen && FAST_CELLULAR_GENERATIONS.has(String(gen).toLowerCase())) {
      isFast = true;
    }
  }

  return { isOnline, isFast, kind };
}

export function useNetworkStatus(): NetworkStatus {
  const [status, setStatus] = useState<NetworkStatus>({
    isOnline: true,
    isFast: true,
    kind: 'unknown',
  });

  useEffect(() => {
    // Lectura inicial
    NetInfo.fetch().then((s) => setStatus(parseNetInfo(s))).catch(() => {});
    // Suscripción a cambios
    const unsub = NetInfo.addEventListener((s) => setStatus(parseNetInfo(s)));
    return () => unsub();
  }, []);

  return status;
}
